/**
 * DCTA-141 — end-of-speech, owned by the app and not by the engine ("people don't always finish a sentence in one go
 * at one speed; the whole sentence must be heard to the end" — Umakhan, 7 Oct).
 *
 * Engine-agnostic on purpose: the input is transcript events (interim or final, with a timestamp), engine restarts
 * and the customer's acts; the output is when listening is over and what happens to the words. The browser's Web
 * Speech engine feeds it today (`adapters/voice.ts`); Tencent real-time ASR (phase 2) feeds it the same events —
 * a sentence's `slice_type` 2 is a final — and the UX does not change. Time is passed in: no timer, no clock, no DOM.
 *
 * The rules (DCTA-141 rulings 2, 3, 5):
 *  - tap = listen until Done, or until adaptive silence: `complete` after a phrase that looks complete, `dangling`
 *    after a word that leaves it hanging or a filler — and after less than `minSpeech` of speech, which never gets
 *    the short wait; nothing ends before `minSpeech` from the first word either; `cap` ends any listening;
 *  - hold = listening while held; pauses never end it; the release does;
 *  - senior (Mdm Lim, large type): silence only stops listening — the words wait for review, never sent on their own;
 *  - the countdown phase is the last `countdown` before an automatic send, so new words can still cancel it.
 */

import { keysOf, stitch } from "../lib/words.js";

export interface EosTuning {
  /** Silence that ends a phrase that looks complete (ms). */
  readonly complete: number;
  /** Silence that ends a phrase left hanging — a dangling word, a filler — or one with under `minSpeech` of speech (ms). */
  readonly dangling: number;
  /** Less speech than this never gets the short wait, and nothing ends sooner than this after the first word (ms). */
  readonly minSpeech: number;
  /** No listening runs longer (ms). */
  readonly cap: number;
  /** The visible countdown before an automatic send (ms). */
  readonly countdown: number;
  /** A pause this long shows "Still listening" (ms). */
  readonly still: number;
  /** Nothing heard this long after the microphone opened: listening ends (ms). */
  readonly nothing: number;
}

export const EOS: EosTuning = { complete: 2500, dangling: 4500, minSpeech: 1500, cap: 60_000, countdown: 1000, still: 1000, nothing: 8000 };

export type EosMode = "tap" | "hold";
/** What the composer shows while listening. */
export type Phase = "waiting" | "hearing" | "still" | "countdown";
export type EndKind = "send" | "review" | "nothing";
export type EndWhy = "done" | "silence" | "release" | "cap" | "edit" | "nothing" | "lost";

/** How a listening ended. `text` is the transcript at that moment. */
export interface Ending {
  readonly kind: EndKind;
  readonly why: EndWhy;
  readonly text: string;
  readonly at: number;
}

// ---------------------------------------------------------------- words
export { stitch };

/** Words that leave a sentence hanging at its end — the brief's list, then their obvious siblings (an, into, or, with, but, at, by, in, on). */
const HANGING = new Set(["to", "and", "from", "for", "of", "the", "a", "my", "then", "also", "plus", "an", "into", "or", "with", "but", "at", "by", "in", "on"]);
/** Fillers: the brief's (um, uh, er, like, wait, hmm) as engines spell them. */
const FILLERS = new Set(["um", "umm", "uh", "uhh", "uhm", "er", "erm", "err", "ah", "eh", "like", "wait", "hmm", "hmmm", "hm", "mm"]);
/** Money verbs: a figure right after one ("send fifty") or one with nothing after ("pay") is not a finished request. */
const VERBS = new Set(["send", "pay", "transfer", "move", "give", "buy", "sell", "invest", "put"]);
/** A scale word at the end means the number may go on ("two hundred" → "two hundred and fifty"). */
const SCALES = new Set(["hundred", "thousand", "million", "billion"]);
const NUMBER_WORDS = new Set([
  "zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten", "eleven", "twelve", "thirteen", "fourteen",
  "fifteen", "sixteen", "seventeen", "eighteen", "nineteen", "twenty", "thirty", "forty", "fifty", "sixty", "seventy", "eighty", "ninety",
]);

function isNumber(word: string): boolean {
  return /^\d[\d,.]*k?$/.test(word) || word.split("-").every((part) => NUMBER_WORDS.has(part));
}

/**
 * Does the transcript stop mid-thought? A dangling word at the end (to, and, from, …), a filler (um, uh, …), a number
 * still being said (a scale word, or a figure right after a money verb: "send fifty") or a money verb with nothing after.
 */
export function dangling(text: string): boolean {
  const words = keysOf(text);
  const last = words.at(-1);
  if (last === undefined) return false;
  if (HANGING.has(last) || FILLERS.has(last) || SCALES.has(last) || VERBS.has(last)) return true;
  const before = words.at(-2);
  return before !== undefined && VERBS.has(before) && isNumber(last);
}

/**
 * DCTA-181 — what the words do once the engine's LAST result is in. An act ends listening; it does not decide on the words
 * the engine had returned by then (Tencent's first words come ~2.2 s after the speech: a quick Done had none, was
 * "nothing", and the engine was dropped with the audio still in it). Done and the release send whatever comes back;
 * the adaptive silence sends (Mdm Lim's waits for review); an end the app did not hear — the 8 s "nothing heard", the
 * cap, a lost engine — and a tap on the words wait for review; no words at all is nothing.
 */
export function settle(ending: Ending, words: string): EndKind {
  if (words.trim() === "") return "nothing";
  if (ending.why === "done" || ending.why === "release") return "send";
  if (ending.why === "silence") return ending.kind === "send" ? "send" : "review";
  return "review";
}

// ---------------------------------------------------------------- the machine
export class EndOfSpeech {
  readonly #t: EosTuning;
  readonly #senior: boolean;
  readonly #start: number;
  #mode: EosMode;
  /** Pieces already settled (finals, and whatever an engine had when it was replaced). */
  #kept = "";
  /** The piece the engine is still saying. */
  #now = "";
  /** The words last counted as speech — a final that only re-confirms them is not speech. */
  #said = "";
  /** How many words that was (DCTA-162: a final with no more words than this re-confirms them). */
  #saidWords = 0;
  #first: number | undefined;
  #last: number | undefined;
  #ended: Ending | undefined;

  constructor(o: { readonly mode: EosMode; readonly senior?: boolean; readonly at: number; readonly tuning?: Partial<EosTuning> }) {
    this.#t = { ...EOS, ...o.tuning };
    this.#senior = o.senior === true;
    this.#start = o.at;
    this.#mode = o.mode;
  }

  get mode(): EosMode {
    return this.#mode;
  }

  get ended(): Ending | undefined {
    return this.#ended;
  }

  /** Everything heard so far, as one sentence. */
  get transcript(): string {
    return stitch(this.#kept, this.#now);
  }

  /** An engine's result: `text` is what it reports for the words in progress; `final` = settled. */
  heard(text: string, final: boolean, at: number): void {
    this.#now = text.trim();
    if (final) {
      this.#kept = stitch(this.#kept, this.#now);
      this.#now = "";
    }
    const keys = keysOf(this.transcript);
    const said = keys.join(" ");
    if (said === this.#said) return;
    const before = this.#saidWords;
    this.#said = said;
    this.#saidWords = keys.length;
    if (said === "" || this.#ended !== undefined) return;
    // DCTA-162 — Tencent's own sentence end: its VAD closes a sentence with a stable result that writes the figures as
    // digits ("fifty" → "50", "four five two one" → "4521") and adds the full stop. Words changed, none were added: it
    // re-confirms what was said, so the silence wait keeps counting from the last word (live run: 4.6 s → 2.5 s).
    // A final that brings more words is speech (an engine that sends finals only).
    if (final && this.#last !== undefined && keys.length <= before) return;
    this.#first ??= at;
    this.#last = at;
  }

  /** The engine stopped by itself and a new one took over: what it had is kept; the next words are a new piece. */
  segment(_at: number): void {
    this.#kept = stitch(this.#kept, this.#now);
    this.#now = "";
  }

  /** The press became a hold: pauses no longer end it. */
  hold(_at: number): void {
    if (this.#ended === undefined) this.#mode = "hold";
  }

  /** The system took a hold's pointer: listening goes on as a tap — silence ends it again, never the lost pointer. */
  tap(_at: number): void {
    if (this.#ended === undefined) this.#mode = "tap";
  }

  release(at: number): Ending {
    return this.#end(this.#words() ? "send" : "nothing", "release", at);
  }

  done(at: number): Ending {
    return this.#end(this.#words() ? "send" : "nothing", "done", at);
  }

  /** A tap on the words: they go to the field for editing, exactly as shown. */
  edit(at: number): Ending {
    return this.#end(this.#words() ? "review" : "nothing", "edit", at);
  }

  /** The engine is gone for good (a restart refused, an engine error): the words wait for review. */
  lost(at: number): Ending {
    return this.#end(this.#words() ? "review" : "nothing", "lost", at);
  }

  phase(at: number): Phase {
    if (this.#last === undefined) return "waiting";
    const due = this.#due();
    if (due !== undefined && !this.#senior && at >= due - this.#t.countdown) return "countdown";
    return at - this.#last >= this.#t.still ? "still" : "hearing";
  }

  /** Time passes: an ending when one is due now. */
  tick(at: number): Ending | undefined {
    if (this.#ended !== undefined) return undefined;
    if (at - this.#start >= this.#t.cap) return this.#end(this.#words() ? "review" : "nothing", "cap", at);
    if (this.#mode === "hold") return undefined;
    if (this.#last === undefined) return at - this.#start >= this.#t.nothing ? this.#end("nothing", "nothing", at) : undefined;
    const due = this.#due();
    if (due !== undefined && at >= due) return this.#end(this.#senior ? "review" : "send", "silence", at);
    return undefined;
  }

  /** The next instant at which the phase changes or an ending falls due — the one timer's next deadline. */
  wake(at: number): number | undefined {
    if (this.#ended !== undefined) return undefined;
    const marks = [this.#start + this.#t.cap];
    if (this.#last !== undefined) marks.push(this.#last + this.#t.still);
    if (this.#mode === "tap") {
      const due = this.#due();
      if (due === undefined) marks.push(this.#start + this.#t.nothing);
      else marks.push(due, ...(this.#senior ? [] : [due - this.#t.countdown]));
    }
    const next = marks.filter((t) => t > at);
    return next.length === 0 ? undefined : Math.min(...next);
  }

  /** When silence ends this listening: tap mode, after the first word. */
  #due(): number | undefined {
    if (this.#mode === "hold" || this.#first === undefined || this.#last === undefined) return undefined;
    const short = this.#last - this.#first < this.#t.minSpeech || dangling(this.transcript);
    return Math.max(this.#last + (short ? this.#t.dangling : this.#t.complete), this.#first + this.#t.minSpeech);
  }

  #words(): boolean {
    return this.transcript !== "";
  }

  #end(kind: EndKind, why: EndWhy, at: number): Ending {
    this.#ended ??= { kind, why, text: this.transcript, at };
    return this.#ended;
  }
}
